import React, { ReactNode, useState } from "react";
import "./ModuleLayout.css";

export interface ModuleTabMeta {
  key: string;
  label: string;
  desc: string;
  help: string;
}

interface ModuleLayoutProps {
  tabs?: ModuleTabMeta[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  renderPanel?: (tab: ModuleTabMeta) => ReactNode;
  aside?: ReactNode; // widgets stack
  className?: string;
  defaultPanelTitleLevel?: number; // for potential a11y flexibility
  hideTabHeader?: boolean; // hide the panel-head and desc below tabs (when components have their own header)
}

const ModuleLayout: React.FC<ModuleLayoutProps> = ({
  tabs = [],
  activeTab,
  onTabChange,
  renderPanel,
  aside,
  className = "",
  defaultPanelTitleLevel = 2,
  hideTabHeader = false,
}) => {
  // dynamic heading level (clamped between h2-h4 for sanity)
  const safeLevel = Math.min(4, Math.max(2, defaultPanelTitleLevel));
  const LevelTag = `h${safeLevel}` as unknown as React.ElementType;
  const current = activeTab || (tabs[0] && tabs[0].key);

  const [asideOpen, setAsideOpen] = useState(false);
  const hasAside = !!aside;
  const showAside = hasAside && asideOpen;

  return (
    <div className={`module-page ${className}`.trim()}>
      <div
        className={`module-layout ${showAside ? "aside-open" : "aside-collapsed"}`}
      >
        <div className="module-main">
          {hasAside && (
            <button
              type="button"
              className="aside-toggle"
              aria-label={showAside ? "Hide widgets" : "Show widgets"}
              aria-expanded={showAside}
              onClick={() => setAsideOpen((o) => !o)}
              title={showAside ? "Hide widgets" : "Show widgets"}
            >
              <span className="aside-toggle-bar" />
              <span className="aside-toggle-bar" />
              <span className="aside-toggle-bar" />
            </button>
          )}
          {tabs.length > 0 && (
            <div className="tabs full-width" role="tablist">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={current === t.key}
                  className={`tab ${current === t.key ? "active" : ""}`}
                  onClick={() => onTabChange && onTabChange(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          <div className="tab-content">
            {tabs.map((t) => (
              <section
                key={t.key}
                role="tabpanel"
                className={`tab-panel ${current === t.key ? "show" : ""}`}
                aria-hidden={current !== t.key}
              >
                {!hideTabHeader && (
                  <>
                    <div className="panel-head">
                      <LevelTag className="panel-title">{t.label}</LevelTag>
                      <div
                        className="panel-help"
                        role="note"
                        aria-label={`About ${t.label}`}
                      >
                        <span
                          className="mini-info-icon"
                          tabIndex={0}
                          aria-haspopup="true"
                          aria-expanded="false"
                          aria-label={`Help for ${t.label}`}
                        >
                          i
                        </span>
                        <div className="panel-tooltip" role="tooltip">
                          {t.help}
                        </div>
                      </div>
                    </div>
                    <p className="panel-desc one-line" title={t.desc}>
                      {t.desc}
                    </p>
                  </>
                )}
                {renderPanel && renderPanel(t)}
              </section>
            ))}
            {tabs.length === 0 && (
              <div className="coming-soon">
                <span className="soon-badge">Coming soon</span>
                <p>
                  This module layout is prepared. Functional tabs will be
                  introduced shortly.
                </p>
              </div>
            )}
          </div>
        </div>
        {hasAside && (
          <aside
            className="module-aside"
            aria-label="Module widgets"
            aria-hidden={!showAside}
          >
            {aside}
          </aside>
        )}
      </div>
    </div>
  );
};

export default ModuleLayout;
